import { useDialog } from '@react-aria/dialog';
import { FocusScope } from '@react-aria/focus';
import { useModal, useOverlay } from '@react-aria/overlays';
import { mergeProps } from '@react-aria/utils';
import { useOverlayTriggerState } from '@react-stately/overlays';
import { cn } from '@up-riac/bespoke-ui';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { HeaderAccountLoginPage } from 'src/components/Header/components/HeaderAccountSection/HeaderAccountLoginPage';
import {
  HeaderAccountProfileSection,
} from 'src/components/Header/components/HeaderAccountSection/HeaderAccountProfileSection';
import { HeaderMobileMenuHeader } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuHeader';
import { HeaderMobileMenuNavItem } from 'src/components/Header/components/HeaderMobileMenu/HeaderMobileMenuNavItem';
import {
  HeaderMobileMenuAuctionsPage,
} from 'src/components/Header/components/HeaderMobileMenu/pages/HeaderMobileMenuAuctionsPage';
import {
  HeaderMobileMenuConsignmentPage,
} from 'src/components/Header/components/HeaderMobileMenu/pages/HeaderMobileMenuConsignmentPage';
import {
  HeaderMobileMenuLatestPage,
} from 'src/components/Header/components/HeaderMobileMenu/pages/HeaderMobileMenuLatestPage';
import {
  HeaderMobileMenuSettingsPage,
} from 'src/components/Header/components/HeaderMobileMenu/pages/HeaderMobileMenuSettingsPage';
import { AccountHeaderContext } from 'src/lib/AccountHeaderContext';
import { LoginContext } from 'src/lib/LoginContext';
import { ScrollContext } from 'src/lib/ScrollContext';

export const HeaderMobileMenu = ({ state }: {state: ReturnType<typeof useOverlayTriggerState>}): JSX.Element => {
  const ref = useRef(null!);
  const scrollRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const { overlayProps } = useOverlay({
    onClose: state.close,
    isOpen: state.isOpen,
    isDismissable: true,
    shouldCloseOnBlur: false,
    shouldCloseOnInteractOutside: (element: HTMLElement) => {
      if(element.closest('[data-dont-close-mega-menu="true"]')) {
        return false;
      }
      if(element.closest('[data-allow-press-while-closing-mega-menu="true"]')) {
        setTimeout(() => state.close(), 100);
        return false;
      }
      return true;
    },
  }, ref);
  const { dialogProps } = useDialog({}, ref);
  const { modalProps } = useModal();

  const [page, setPage] = useState<'main' | 'login' | 'auctions' | 'consignment' | 'latest' | 'company' | 'locations' | 'account' | 'account/profile' | 'account/consignment' | 'account/settings' | 'account/buying'>('main');
  const { isLoggedIn } = useContext(LoginContext);
  const { messages, bidCart, bidsSubmitted } = useContext(AccountHeaderContext);

  useEffect(() => {
    if(isLoggedIn) {
      if(page === 'login') {
        setPage('main');
      }
    } else {
      if(page === 'account') {
        setPage('main');
      }
    }
  }, [isLoggedIn, page]);


  // Adjust padding when header size changes
  // Unlike most paddings, also cares about top offset.
  const { headerRef, headerPadding } = useContext(ScrollContext);
  const [padding, setPadding] = useState(headerPadding);
  const [needsBottomPadding, setNeedsBottomPadding] = useState(false);

  useEffect(() => {
    setPadding(headerPadding + headerRef.current?.getBoundingClientRect().top || 0);
  }, [headerPadding]);

  // Check if content exceeds available space
  useEffect(() => {
    const checkContentHeight = () => {
      if(contentRef.current && state.isOpen) {
        const chatButtonSize = 56; // 48px chat button + 8px padding
        const availableHeight = window.innerHeight - padding - chatButtonSize;
        const contentHeight = contentRef.current.scrollHeight;
        setNeedsBottomPadding(contentHeight > availableHeight);
      }
    };

    checkContentHeight();
    const resizeObserver = new ResizeObserver(checkContentHeight);

    if(contentRef.current) {
      resizeObserver.observe(contentRef.current);
    }

    return () => resizeObserver.disconnect();
  }, [padding, page, state.isOpen]);

  // Scroll to top when page changes
  // Notable on mobile when navigating between pages
  useEffect(() => {
    if(scrollRef.current) {
      scrollRef.current.scrollTop = 0;
    }
  }, [page]);

  return (
    <FocusScope restoreFocus contain autoFocus>
      <div
        {...mergeProps(overlayProps, dialogProps, modalProps)}
        ref={ref}
        className={cn(
          'shadow-md shadow-black/50 bg-surface-dark',
          'absolute z-50 right-0 mt-[2px] md:mt-0',
          'top-full h-screen md:h-auto w-full md:max-w-[420px] md:mr-10 focus:outline-0 overflow-y-auto'
        )}
        style={{
          maxHeight: `calc(100vh - ${padding}px)`,
        }}
      >
        <div ref={scrollRef} className='h-full overflow-y-auto'>
          <div
            ref={contentRef}
            className={cn(needsBottomPadding && 'pb-[56px]')}
          >
            {page === 'main' && <>
              {!isLoggedIn
                && <HeaderMobileMenuNavItem id='header-mobile-menu-login-item' label='Log in or create account' onPress={() => setPage('login')}/>
              || <>
                <div className='pt-6 pb-3'>
                  <HeaderAccountProfileSection id='header-mobile-menu-profile' noBorderBottom onClose={state.toggle} currentPage={page}/>
                </div>
                <HeaderMobileMenuNavItem id='header-mobile-menu-auctions-item' label='Account' onPress={() => setPage('account')}/>
              </>
              }
              <HeaderMobileMenuNavItem id='header-mobile-menu-auctions-item' label='Auctions' onPress={() => setPage('auctions')}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-consignment-item' label='Consignment' onPress={() => setPage('consignment')}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-latest-item' label='Latest' onPress={() => setPage('latest')}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-company-item' label='Company' onPress={() => setPage('company')}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-faq-item' noArrow label='FAQ' to='/faq' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-contact-item' noArrow label='Contact Us' to='/contact' onPress={() => state.close()}/>
            </>}
            {page === 'login' && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-login-page-header'
                onBack={() => setPage('main')}
                to='/login'
                onPress={state.toggle}
              >
                Log in
              </HeaderMobileMenuHeader>
              <HeaderAccountProfileSection id='header-mobile-menu-login-page-profile' onClose={state.toggle} currentPage={page}/>
              <div className='px-4 pt-[32px] pb-5'>
                <HeaderAccountLoginPage id={'header-mobile-menu-login-page-form'} onClose={state.close}/>
              </div>
            </>}
            {page === 'auctions' && <>
              <HeaderMobileMenuAuctionsPage setPage={setPage} closeMenu={() => state.close()}/>
            </>}
            {page === 'consignment' && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-consignment-page-header'
                onBack={() => setPage('main')}
                to='/consignment'
                onPress={state.toggle}
              >
                Consignment
              </HeaderMobileMenuHeader>
              <HeaderMobileMenuNavItem id='header-mobile-menu-consignment-process-item' label='Consignment Process' noArrow to='/consignment/consignment-process' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-consignment-where-legends-come-to-sell-item' label='Where Legends Come to Sell' noArrow to='/consignment/notable-collectors' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-consignment-market-defining-results-item' label='Market Defining Results' noArrow to='/consignment/past-auction-successes' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-consignment-market-defining-start-selling-item' label='Start Selling' noArrow to='/consignment/getting-started' onPress={() => state.close()}/>
            </>}
            {page === 'latest' && <>
              <HeaderMobileMenuLatestPage setPage={setPage} closeMenu={() => state.close()}/>
            </>}
            {page === 'company' && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-company-page-header'
                onBack={() => setPage('main')}
                to='/about-us'
                onPress={() => state.close()}
              >
                Company
              </HeaderMobileMenuHeader>
              <HeaderMobileMenuNavItem id='header-mobile-menu-company-about-item' label='About Us' noArrow to='/about-us' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-company-past-that-drives-us-item' label='The Past That Drives Us' noArrow to='/consignment/the-past-that-drives-us' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-company-careers-item' label='Careers' noArrow to='/careers' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-company-locations-item' label='Locations' onPress={() => setPage('locations')}/>
            </>}
            {page === 'locations' && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-locations-page-header'
                onBack={() => setPage('company')}
                to='/about-us'
                onPress={() => state.close()}
              >
                Locations
              </HeaderMobileMenuHeader>
              <HeaderMobileMenuNavItem id='header-mobile-menu-locations-item-1' label='Bedford, TX' noArrow description='Our international headquarters at 3600 Harwood Rd, Bedford, TX is the finest auction venue in the world.' to='/location/bedford' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-locations-item-2' label='Rock Island, IL' noArrow description='Our facilities at 7819 42nd St W, Rock Island, IL oversee every step of our production process.'/>
            </>}
            {page === 'account' && isLoggedIn && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-account-page-header'
                onBack={() => setPage('main')}
                to='/account'
                onPress={() => state.close()}
              >
                Account
              </HeaderMobileMenuHeader>
              <HeaderMobileMenuNavItem id='header-mobile-menu-account-profile-item' label='Profile' onPress={() => setPage('account/profile')}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-account-messages-item' notificationCount={messages} label='Messages' noArrow to='/account/messages' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-account-buying-item' label='Buying' onPress={() => setPage('account/buying')}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-account-consigning-item' label='Consigning' onPress={() => setPage('account/consignment')}/>
              <HeaderMobileMenuNavItem id='header-mobile-menu-account-settings-item' label='Settings' onPress={() => setPage('account/settings')}/>
            </>}
            {page === 'account/settings' && isLoggedIn && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-account-settings-page-header'
                onBack={() => setPage('account')}
                to='/account'
                onPress={() => state.close()}
              >
                Settings
              </HeaderMobileMenuHeader>
              <HeaderMobileMenuSettingsPage/>
            </>}
            {page === 'account/profile' && isLoggedIn && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-account-profile-page-header'
                onBack={() => setPage('account')}
                to='/account'
                onPress={() => state.close()}
              >
                Profile
              </HeaderMobileMenuHeader>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-profile-page-profile-item' label='Profile' to='/account' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-profile-page-address-book-item' label='Address Book' to='/account/address-book' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-profile-page-phone-book-item' label='Phone Book' to='/account/phone-book' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-profile-page-credit-cards-item' label='Credit Cards' to='/account/credit-cards' onPress={() => state.close()}/>
            </>}
            {page === 'account/buying' && isLoggedIn && <>
              <HeaderMobileMenuHeader
                id='header-mobile-menu-account-buying-page-header'
                onBack={() => setPage('account')}
                to='/account'
                onPress={() => state.close()}
              >
                Buying
              </HeaderMobileMenuHeader>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-buying-page-my-bids-item' notificationCount={bidsSubmitted} label='My Bids' to='/account/bids' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-buying-page-won-auctions-item' label='Won Auctions' to='/account/won-auctions' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-buying-page-bid-cart-item' notificationCount={bidCart} label='Bid Cart' to='/account/cart' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-buying-page-wishlists-item' label='Wishlists' to='/account/wishlist' onPress={() => state.close()}/>
              <HeaderMobileMenuNavItem noArrow id='header-mobile-menu-account-buying-page-text-alerts-item' label='Text Alerts' to='/account/textalerts' onPress={() => state.close()}/>
            </>}
            {page === 'account/consignment' && isLoggedIn && <>
              <HeaderMobileMenuConsignmentPage setPage={setPage} closeMenu={() => state.close()}/>
            </>}
          </div>
        </div>
      </div>
    </FocusScope>
  );
};
